import { useMemo, useState } from 'react';
import { useSession, useSwitchTeam } from '@/hooks/useSession';
import { useSaveTimezone, useTimezone } from '@/hooks/useSettings';
import { useTheme } from '@/components/theme';
import { Screen } from '@m/ui/Screen';
import { Group, Row } from '@m/ui/List';
import { Segmented } from '@m/ui/Controls';
import { ActionSheet } from '@m/ui/Sheet';
import { Picker } from '@m/ui/Picker';

/** Appearance, timezone, team and the account this phone is signed in as. */
export function SettingsScreen() {
  const session = useSession();
  const { theme, setTheme } = useTheme();
  const timezone = useTimezone().data ?? session.user.timezone;
  const saveTimezone = useSaveTimezone();
  const switchTeam = useSwitchTeam();
  const [pickingZone, setPickingZone] = useState(false);
  const [pickingTeam, setPickingTeam] = useState(false);

  const zones = useMemo(() => {
    const all = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] }).supportedValuesOf?.('timeZone') ?? [timezone];
    return all.map((z) => ({ id: z, name: z.replace(/_/g, ' ') }));
  }, [timezone]);
  const deviceZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  return (
    <Screen title="Settings" back="/more" backLabel="More">
      <Group header="Account">
        <Row title="Name" detail={session.user.name ?? '—'} />
        <Row title="Email" detail={session.user.email ?? '—'} />
      </Group>

      <Group header="Team" footer={session.teams.length > 1 ? 'Everything you see is the team chosen here.' : undefined}>
        <Row
          title={session.team?.name ?? 'No team'}
          subtitle={session.role === 'admin' ? 'Admin' : 'Member'}
          detail={session.teams.length > 1 ? 'Switch' : undefined}
          onClick={session.teams.length > 1 ? () => setPickingTeam(true) : undefined}
          chevron={session.teams.length > 1}
        />
      </Group>

      <Group header="Appearance">
        <div className="p-3">
          <Segmented
            value={theme}
            onChange={setTheme}
            options={[
              { value: 'system', label: 'System' },
              { value: 'light', label: 'Light' },
              { value: 'dark', label: 'Dark' }
            ]}
          />
        </div>
      </Group>

      <Group header="Time zone" footer="Days on your timesheet start and end in this zone.">
        <Row title="Time zone" detail={timezone.replace(/_/g, ' ')} onClick={() => setPickingZone(true)} chevron />
        {deviceZone !== timezone && (
          <Row title={`Use this phone's zone (${deviceZone.replace(/_/g, ' ')})`} accent onClick={() => saveTimezone.mutate(deviceZone)} />
        )}
      </Group>

      <Picker
        open={pickingZone}
        onClose={() => setPickingZone(false)}
        title="Time zone"
        backLabel="Settings"
        items={zones}
        recent={zones.filter((z) => z.id === deviceZone)}
        value={timezone}
        noun="time zone"
        onSelect={(z) => saveTimezone.mutate(z.id)}
      />
      <ActionSheet
        open={pickingTeam}
        onClose={() => setPickingTeam(false)}
        title="Switch team"
        actions={session.teams
          .filter((t) => t.id !== session.team?.id)
          .map((t) => ({ label: t.name, onSelect: () => switchTeam.mutate(t.id) }))}
      />
    </Screen>
  );
}
